import {
  ASSESSMENT_SECTIONS,
  QUESTION_INDEX,
  RETIRED_QUESTION_IDS,
} from './content.js';

const FORM_ERROR = 'We could not check these answers. Please refresh and try again.';
const UNKNOWN_QUESTION_ERROR = 'This answer is not part of this assessment version.';
const RETIRED_QUESTION_ID_SET = new Set(RETIRED_QUESTION_IDS);

function result(errors) {
  return { valid: Object.keys(errors).length === 0, errors };
}

function isPlainObject(value) {
  if (value === null || typeof value !== 'object' || Array.isArray(value)) return false;
  const prototype = Object.getPrototypeOf(value);
  return prototype === Object.prototype || prototype === null;
}

function hasOnlyKeys(value, allowedKeys) {
  return Object.keys(value).every((key) => allowedKeys.includes(key));
}

function normalisedText(value) {
  return typeof value === 'string' ? value.trim() : '';
}

function wordCount(value) {
  const normalised = normalisedText(value);
  return normalised ? normalised.split(/\s+/u).length : 0;
}

function validDate(value) {
  if (typeof value !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const [year, month, day] = value.split('-').map(Number);
  const date = new Date(Date.UTC(year, month - 1, day));
  return (
    date.getUTCFullYear() === year &&
    date.getUTCMonth() === month - 1 &&
    date.getUTCDate() === day
  );
}

function minutesFromTime(value) {
  if (typeof value !== 'string' || !/^([01]\d|2[0-3]):[0-5]\d$/.test(value)) {
    return null;
  }
  const [hours, minutes] = value.split(':').map(Number);
  return hours * 60 + minutes;
}

function textError(question, value) {
  if (!question.required && value === undefined) return null;
  if (typeof value !== 'string') return 'Enter your answer as text.';

  const text = normalisedText(value);
  if (question.required && text.length === 0) return 'Add an answer before continuing.';
  if (text.length === 0) return null;
  if (text.length < question.minLength) {
    return `Add a little more detail (${question.minLength} characters minimum).`;
  }
  if (text.length > question.maxLength) {
    return `Shorten this answer to ${question.maxLength} characters or fewer.`;
  }
  if (question.maxWords && wordCount(text) > question.maxWords) {
    return `Shorten this answer to ${question.maxWords} words or fewer.`;
  }
  return null;
}

function capabilityGridError(question, value) {
  if (!isPlainObject(value)) return 'Rate each capability before continuing.';
  const capabilityIds = question.capabilities.map(({ id }) => id);
  if (!hasOnlyKeys(value, capabilityIds)) return 'Check the capability ratings and try again.';

  for (const capability of question.capabilities) {
    const answer = value[capability.id];
    if (!isPlainObject(answer) || !hasOnlyKeys(answer, ['rating', 'evidence'])) {
      return `Choose a rating for ${capability.label}.`;
    }
    if (!question.options.some(({ id }) => id === answer.rating)) {
      return `Choose a rating for ${capability.label}.`;
    }
    if (answer.evidence !== undefined && typeof answer.evidence !== 'string') {
      return `Check the evidence for ${capability.label}.`;
    }
    if (normalisedText(answer.evidence).length > 1800) {
      return `Shorten the evidence for ${capability.label} to 1800 characters or fewer.`;
    }
    if (
      answer.rating === question.highestRatingId &&
      normalisedText(answer.evidence).length === 0
    ) {
      return `Add a concrete beyond-one-venue example for ${capability.label}.`;
    }
  }
  return null;
}

function evidencePairError(question, value) {
  if (!Array.isArray(value) || value.length !== question.itemCount) {
    return 'Choose a capability for Example 1.';
  }
  const capabilityIds = new Set(question.capabilities.map(({ id }) => id));
  const selected = new Set();

  for (const [index, item] of value.entries()) {
    const exampleNumber = index + 1;
    if (!isPlainObject(item) || !hasOnlyKeys(item, ['capabilityId', 'response'])) {
      return `Complete Example ${exampleNumber}: choose a capability and add your evidence.`;
    }
    if (!capabilityIds.has(item.capabilityId)) {
      return `Choose a capability for Example ${exampleNumber}.`;
    }
    if (selected.has(item.capabilityId)) {
      return `Choose a different capability for Example ${exampleNumber}. Each example must use a different capability.`;
    }
    selected.add(item.capabilityId);
    if (typeof item.response !== 'string' || normalisedText(item.response).length === 0) {
      return `Add your evidence for Example ${exampleNumber}.`;
    }
    const responseLength = normalisedText(item.response).length;
    if (responseLength < question.minLength) {
      return `Add a little more detail to Example ${exampleNumber} (${question.minLength} characters minimum).`;
    }
    if (responseLength > question.maxLength) {
      return `Shorten Example ${exampleNumber} to ${question.maxLength} characters or fewer.`;
    }
  }
  return null;
}

function commitmentGridError(question, value) {
  if (!isPlainObject(value)) return 'Answer each practical commitment before continuing.';
  const commitmentIds = question.commitments.map(({ id }) => id);
  if (!hasOnlyKeys(value, commitmentIds)) return 'Check the commitment answers and try again.';

  for (const commitment of question.commitments) {
    const answer = value[commitment.id];
    if (!isPlainObject(answer) || !hasOnlyKeys(answer, ['choice', 'explanation'])) {
      return 'Choose an answer for every practical commitment.';
    }
    if (!question.options.some(({ id }) => id === answer.choice)) {
      return 'Choose an answer for every practical commitment.';
    }
    if (answer.explanation !== undefined && typeof answer.explanation !== 'string') {
      return 'Enter the practical constraint as text.';
    }
    const explanation = normalisedText(answer.explanation);
    if (explanation.length > question.explanationMaxLength) {
      return `Shorten each explanation to ${question.explanationMaxLength} characters or fewer.`;
    }
    if (
      answer.choice !== 'yes' &&
      explanation.length < question.explanationMinLength
    ) {
      return `Explain each constraint or no answer in at least ${question.explanationMinLength} characters.`;
    }
  }
  return null;
}

function optionsError(question, value, { exactCount = null } = {}) {
  if (!Array.isArray(value)) return 'Choose from the available options.';
  if (exactCount !== null && value.length !== exactCount) {
    return `Choose and order exactly ${exactCount} different options.`;
  }
  if (value.length < (question.minItems ?? 0)) return 'Choose at least one option.';
  if (question.maxItems && value.length > question.maxItems) {
    return `Choose no more than ${question.maxItems} options.`;
  }
  if (new Set(value).size !== value.length) return 'Choose each option only once.';
  const optionIds = new Set(question.options.map(({ id }) => id));
  if (value.some((id) => typeof id !== 'string' || !optionIds.has(id))) {
    return 'Choose from the available options.';
  }
  return null;
}

function dateRangesError(question, value) {
  if (
    !isPlainObject(value) ||
    !hasOnlyKeys(value, ['selection', 'ranges']) ||
    !['none', 'planned'].includes(value.selection) ||
    !Array.isArray(value.ranges)
  ) {
    return 'Tell us whether you have planned leave.';
  }
  if (value.selection === 'none') {
    return value.ranges.length === 0 ? null : 'Remove the leave dates or choose planned leave.';
  }
  if (value.ranges.length === 0) return 'Add at least one planned leave date range.';

  for (const range of value.ranges) {
    if (
      !isPlainObject(range) ||
      !hasOnlyKeys(range, ['startDate', 'endDate', 'details']) ||
      !validDate(range.startDate) ||
      !validDate(range.endDate) ||
      range.endDate < range.startDate ||
      (question.latestDate && range.endDate > question.latestDate)
    ) {
      return 'Check each planned leave date range.';
    }
    if (typeof range.details !== 'string' || normalisedText(range.details).length === 0) {
      return 'Add short details for each planned leave date range.';
    }
    if (normalisedText(range.details).length > question.detailsMaxLength) {
      return `Shorten leave details to ${question.detailsMaxLength} characters or fewer.`;
    }
  }
  return null;
}

function interviewWindowsError(question, value) {
  if (!Array.isArray(value) || value.length < question.minItems) {
    return `Add at least ${question.minItems} interview windows.`;
  }
  const seen = new Set();
  for (const window of value) {
    if (
      !isPlainObject(window) ||
      !hasOnlyKeys(window, ['date', 'startTime', 'endTime']) ||
      !validDate(window.date)
    ) {
      return 'Check each interview date and time.';
    }
    const start = minutesFromTime(window.startTime);
    const end = minutesFromTime(window.endTime);
    const duration = start === null || end === null ? null : end - start;
    if (
      duration === null ||
      duration < question.minMinutes ||
      duration > question.maxMinutes
    ) {
      return `Each interview window must be ${question.minMinutes}-${question.maxMinutes} minutes.`;
    }
    const key = `${window.date}|${window.startTime}|${window.endTime}`;
    if (seen.has(key)) return 'Add different interview windows.';
    seen.add(key);
  }
  return null;
}

function declarationsError(question, value) {
  if (!isPlainObject(value)) return 'Confirm every declaration before submitting.';
  const declarationIds = question.declarations.map(({ id }) => id);
  if (!hasOnlyKeys(value, declarationIds)) {
    return 'Check the declarations and try again.';
  }
  if (declarationIds.some((id) => value[id] !== true)) {
    return 'Confirm every declaration before submitting.';
  }
  return null;
}

function draftTextError(question, value) {
  if (typeof value !== 'string') return 'Enter your answer as text.';
  const text = normalisedText(value);
  if (text.length > question.maxLength) {
    return `Shorten this answer to ${question.maxLength} characters or fewer.`;
  }
  if (question.maxWords && wordCount(text) > question.maxWords) {
    return `Shorten this answer to ${question.maxWords} words or fewer.`;
  }
  return null;
}

function draftCapabilityGridError(question, value) {
  if (!isPlainObject(value)) return 'Rate each capability before continuing.';
  const capabilityIds = question.capabilities.map(({ id }) => id);
  if (!hasOnlyKeys(value, capabilityIds)) return 'Check the capability ratings and try again.';

  for (const [capabilityId, answer] of Object.entries(value)) {
    const capability = question.capabilities.find(({ id }) => id === capabilityId);
    if (!isPlainObject(answer) || !hasOnlyKeys(answer, ['rating', 'evidence'])) {
      return `Choose a rating for ${capability.label}.`;
    }
    if (
      answer.rating !== undefined &&
      answer.rating !== '' &&
      !question.options.some(({ id }) => id === answer.rating)
    ) {
      return `Choose a rating for ${capability.label}.`;
    }
    if (answer.evidence !== undefined && typeof answer.evidence !== 'string') {
      return `Check the evidence for ${capability.label}.`;
    }
    if (normalisedText(answer.evidence).length > 1800) {
      return `Shorten the evidence for ${capability.label} to 1800 characters or fewer.`;
    }
  }
  return null;
}

function draftEvidencePairError(question, value) {
  if (!Array.isArray(value) || value.length > question.itemCount) {
    return `Choose exactly ${question.itemCount} different capabilities.`;
  }
  const capabilityIds = new Set(question.capabilities.map(({ id }) => id));
  const selected = new Set();

  for (const item of value) {
    if (!isPlainObject(item) || !hasOnlyKeys(item, ['capabilityId', 'response'])) {
      return 'Choose a valid capability for each example.';
    }
    if (
      item.capabilityId !== undefined &&
      item.capabilityId !== '' &&
      !capabilityIds.has(item.capabilityId)
    ) {
      return 'Choose a valid capability for each example.';
    }
    if (item.capabilityId) {
      if (selected.has(item.capabilityId)) return 'Choose two different capabilities.';
      selected.add(item.capabilityId);
    }
    if (item.response !== undefined && typeof item.response !== 'string') {
      return 'Enter your answer as text.';
    }
    if (normalisedText(item.response).length > question.maxLength) {
      return `Shorten this answer to ${question.maxLength} characters or fewer.`;
    }
  }
  return null;
}

function draftCommitmentGridError(question, value) {
  if (!isPlainObject(value)) return 'Answer each practical commitment before continuing.';
  const commitmentIds = question.commitments.map(({ id }) => id);
  if (!hasOnlyKeys(value, commitmentIds)) return 'Check the commitment answers and try again.';

  for (const answer of Object.values(value)) {
    if (!isPlainObject(answer) || !hasOnlyKeys(answer, ['choice', 'explanation'])) {
      return 'Choose an answer for every practical commitment.';
    }
    if (
      answer.choice !== undefined &&
      answer.choice !== '' &&
      !question.options.some(({ id }) => id === answer.choice)
    ) {
      return 'Choose an answer for every practical commitment.';
    }
    if (answer.explanation !== undefined && typeof answer.explanation !== 'string') {
      return 'Enter the practical constraint as text.';
    }
    if (normalisedText(answer.explanation).length > question.explanationMaxLength) {
      return `Shorten each explanation to ${question.explanationMaxLength} characters or fewer.`;
    }
  }
  return null;
}

function draftOptionsError(question, value, { maxItems = question.maxItems } = {}) {
  if (!Array.isArray(value)) return 'Choose from the available options.';
  if (maxItems && value.length > maxItems) {
    return `Choose no more than ${maxItems} options.`;
  }
  if (new Set(value).size !== value.length) return 'Choose each option only once.';
  const optionIds = new Set(question.options.map(({ id }) => id));
  if (value.some((id) => typeof id !== 'string' || !optionIds.has(id))) {
    return 'Choose from the available options.';
  }
  return null;
}

function draftDateError(value) {
  if (value === '') return null;
  return validDate(value) ? null : 'Choose a valid date.';
}

function draftDateStringError(value) {
  if (value === undefined || value === '') return null;
  return typeof value === 'string' && validDate(value)
    ? null
    : 'Check each planned leave date range.';
}

function draftTimeStringError(value) {
  if (value === undefined || value === '') return null;
  return typeof value === 'string' && minutesFromTime(value) !== null
    ? null
    : 'Check each interview date and time.';
}

function draftDateRangesError(question, value) {
  if (
    !isPlainObject(value) ||
    !hasOnlyKeys(value, ['selection', 'ranges']) ||
    !Array.isArray(value.ranges)
  ) {
    return 'Tell us whether you have planned leave.';
  }
  if (
    value.selection !== undefined &&
    value.selection !== '' &&
    !['none', 'planned'].includes(value.selection)
  ) {
    return 'Tell us whether you have planned leave.';
  }
  for (const range of value.ranges) {
    if (!isPlainObject(range) || !hasOnlyKeys(range, ['startDate', 'endDate', 'details'])) {
      return 'Check each planned leave date range.';
    }
    const startError = draftDateStringError(range.startDate);
    const endError = draftDateStringError(range.endDate);
    if (startError || endError) return startError || endError;
    if (
      range.startDate &&
      range.endDate &&
      range.endDate < range.startDate
    ) {
      return 'Check each planned leave date range.';
    }
    if (range.endDate && question.latestDate && range.endDate > question.latestDate) {
      return 'Check each planned leave date range.';
    }
    if (range.details !== undefined && typeof range.details !== 'string') {
      return 'Add short details for each planned leave date range.';
    }
    if (normalisedText(range.details).length > question.detailsMaxLength) {
      return `Shorten leave details to ${question.detailsMaxLength} characters or fewer.`;
    }
  }
  return null;
}

function draftInterviewWindowsError(question, value) {
  if (!Array.isArray(value)) return `Add at least ${question.minItems} interview windows.`;
  const seen = new Set();
  for (const window of value) {
    if (!isPlainObject(window) || !hasOnlyKeys(window, ['date', 'startTime', 'endTime'])) {
      return 'Check each interview date and time.';
    }
    if (draftDateStringError(window.date)) return 'Check each interview date and time.';
    if (draftTimeStringError(window.startTime) || draftTimeStringError(window.endTime)) {
      return 'Check each interview date and time.';
    }
    if (window.startTime && window.endTime) {
      const duration = minutesFromTime(window.endTime) - minutesFromTime(window.startTime);
      if (duration < question.minMinutes || duration > question.maxMinutes) {
        return `Each interview window must be ${question.minMinutes}-${question.maxMinutes} minutes.`;
      }
    }
    if (window.date && window.startTime && window.endTime) {
      const key = `${window.date}|${window.startTime}|${window.endTime}`;
      if (seen.has(key)) return 'Add different interview windows.';
      seen.add(key);
    }
  }
  return null;
}

function draftDeclarationsError(question, value) {
  if (!isPlainObject(value)) return 'Confirm every declaration before submitting.';
  const declarationIds = question.declarations.map(({ id }) => id);
  if (!hasOnlyKeys(value, declarationIds)) return 'Check the declarations and try again.';
  if (Object.values(value).some((answer) => typeof answer !== 'boolean')) {
    return 'Confirm every declaration before submitting.';
  }
  return null;
}

export function validateAnswer(question, value) {
  switch (question.type) {
    case 'acknowledgement':
      return value === true ? null : 'Confirm that you are ready to begin.';
    case 'capability-grid':
      return capabilityGridError(question, value);
    case 'evidence-pair':
      return evidencePairError(question, value);
    case 'commitment-grid':
      return commitmentGridError(question, value);
    case 'single-choice':
      return typeof value === 'string' && question.options.some(({ id }) => id === value)
        ? null
        : 'Choose one of the available options.';
    case 'multi-choice':
      return optionsError(question, value);
    case 'rank':
      return optionsError(question, value, { exactCount: question.rankCount });
    case 'text':
      return textError(question, value);
    case 'date':
      return validDate(value) ? null : 'Choose a valid date.';
    case 'date-ranges':
      return dateRangesError(question, value);
    case 'interview-windows':
      return interviewWindowsError(question, value);
    case 'support-priorities': {
      const error = optionsError(question, value);
      if (error) return error;
      if (
        value.includes(question.exclusiveOptionId) &&
        value.length > 1
      ) {
        return 'Choose no specific priority on its own, or choose up to three support areas.';
      }
      return null;
    }
    case 'declarations':
      return declarationsError(question, value);
    default:
      return FORM_ERROR;
  }
}

export function validateDraftAnswer(question, value) {
  switch (question.type) {
    case 'acknowledgement':
      return typeof value === 'boolean' ? null : 'Confirm that you are ready to begin.';
    case 'capability-grid':
      return draftCapabilityGridError(question, value);
    case 'evidence-pair':
      return draftEvidencePairError(question, value);
    case 'commitment-grid':
      return draftCommitmentGridError(question, value);
    case 'single-choice':
      return value === '' || (typeof value === 'string' && question.options.some(({ id }) => id === value))
        ? null
        : 'Choose one of the available options.';
    case 'multi-choice':
      return draftOptionsError(question, value);
    case 'rank':
      return draftOptionsError(question, value, { maxItems: question.rankCount });
    case 'text':
      return draftTextError(question, value);
    case 'date':
      return draftDateError(value);
    case 'date-ranges':
      return draftDateRangesError(question, value);
    case 'interview-windows':
      return draftInterviewWindowsError(question, value);
    case 'support-priorities': {
      const error = draftOptionsError(question, value);
      if (error) return error;
      if (value.includes(question.exclusiveOptionId) && value.length > 1) {
        return 'Choose no specific priority on its own, or choose up to three support areas.';
      }
      return null;
    }
    case 'declarations':
      return draftDeclarationsError(question, value);
    default:
      return FORM_ERROR;
  }
}

function commitmentNeedsDisclosure(answers) {
  const commitments = answers['reality.commitments'];
  if (!isPlainObject(commitments)) return false;
  return Object.values(commitments).some(
    (answer) => isPlainObject(answer) && ['constraint', 'no'].includes(answer.choice),
  );
}

function validateQuestions(questions, answers) {
  const errors = {};
  for (const question of questions) {
    let error = validateAnswer(question, answers[question.id]);
    if (
      !error &&
      question.requiredWhenCommitmentHasConstraint &&
      commitmentNeedsDisclosure(answers) &&
      normalisedText(answers[question.id]).length === 0
    ) {
      error = 'Tell us what we should understand about the practical constraint.';
    }
    if (error) errors[question.id] = error;
  }
  return errors;
}

function addUnknownQuestionErrors(errors, answers) {
  for (const id of Object.keys(answers)) {
    if (!QUESTION_INDEX[id] && !RETIRED_QUESTION_ID_SET.has(id)) {
      errors[id] = UNKNOWN_QUESTION_ERROR;
    }
  }
  return errors;
}

export function validateSection(sectionId, answers) {
  const section = ASSESSMENT_SECTIONS.find(({ id }) => id === sectionId);
  if (!section || !isPlainObject(answers)) return result({ _form: FORM_ERROR });
  return result(addUnknownQuestionErrors(validateQuestions(section.questions, answers), answers));
}

export function validateAssessment(answers) {
  if (!isPlainObject(answers)) return result({ _form: FORM_ERROR });

  return result(
    addUnknownQuestionErrors(
      validateQuestions(Object.values(QUESTION_INDEX), answers),
      answers,
    ),
  );
}

export function validatePatchAnswers(updates) {
  if (!isPlainObject(updates)) return result({ _form: FORM_ERROR });

  const errors = {};
  for (const [id, value] of Object.entries(updates)) {
    const question = QUESTION_INDEX[id];
    if (!question) {
      if (RETIRED_QUESTION_ID_SET.has(id)) continue;
      errors[id] = UNKNOWN_QUESTION_ERROR;
      continue;
    }
    const error = validateDraftAnswer(question, value);
    if (error) errors[id] = error;
  }
  return result(errors);
}
